// src/modules/callSync/components/GlobalCallBar.jsx
//
// The one place the CRM says "your phone is on a call right now".
//
// It is deliberately NOT attached to a table row or a drawer. The call belongs
// to the user, so the indicator has to survive closing the drawer, changing
// page and — the case this was built for — reloading the browser. Anchored to
// the viewport it is also the same component on a phone, where a banner inside
// a scrolled table would simply never be seen.

import React from 'react';
import { Button, Tag, Typography, Grid, Space, Tooltip } from 'antd';
import {
  PhoneFilled, CloseCircleOutlined, LoadingOutlined, CheckCircleFilled,
  WarningFilled, CloseOutlined,
} from '@ant-design/icons';
import { statusMeta, formatClock, formatDuration, isMeasured } from '../constants';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export default function GlobalCallBar({
  call, error, liveSeconds, stopAction, outcomeOwed, awaitingResult,
  onCancel, onDismiss, onAddOutcome,
}) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // A live call owns the bar. An outcome owed by an earlier call waits its turn
  // rather than covering the call that is happening right now.
  const showOwed = !call && !error && !!outcomeOwed;
  // Calls beyond the one named above that are still owed a result today. Under a
  // result-saved target rule these have earned nothing yet, so the rep is told
  // how many — a nudge, never a block.
  const backlog = Math.max(0, (awaitingResult?.pendingCount || 0) - (showOwed ? 1 : 0));
  const lapsed = awaitingResult?.lapsedCount || 0;
  if (!call && !error && !showOwed) return null;

  const meta = call ? statusMeta(call.status) : null;
  const live = !!meta?.live;
  const owedTone = '#d46b08';
  const connected = !!call && (call.status === 'CONNECTED' || (call.connectedAt && !call.endedAt));
  const failed = !!call && ['FAILED', 'EXPIRED'].includes(call.status);
  // Only a MEASURED figure is real talk time — telecom (the phone is the
  // default dialer and saw the customer answer) or the call log. Anything else
  // is an upper bound that includes far-end ringing, and is labelled as such.
  const measured = isMeasured(call);

  const leadName = call?.lead?.name;
  const number = call?.phoneNumber;

  const tone = showOwed ? owedTone
    : error || failed ? '#cf1322'
      : connected ? '#237804'
        : live ? '#0958d9'
          : '#595959';

  // Both variants sit at the bottom, out of the way of the app header and the
  // page's own actions: full-width on a phone (where the thumb is), and a
  // floating card on desktop.
  const wrapper = isMobile
    ? {
      position: 'fixed',
      zIndex: 1100,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#fff',
      borderTop: `3px solid ${tone}`,
      boxShadow: '0 -2px 12px rgba(0,0,0,0.15)',
      padding: '10px 12px calc(10px + env(safe-area-inset-bottom))',
    }
    : {
      position: 'fixed',
      zIndex: 1100,
      right: 24,
      bottom: 24,
      width: 400,
      maxWidth: 'calc(100vw - 48px)',
      background: '#fff',
      borderRadius: 8,
      borderLeft: `4px solid ${tone}`,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
      padding: '10px 14px',
    };

  const icon = error || failed ? <WarningFilled style={{ color: tone }} />
    : showOwed ? <CheckCircleFilled style={{ color: tone }} />
      : connected ? <PhoneFilled style={{ color: tone }} />
        : live ? <LoadingOutlined style={{ color: tone }} />
          : <CheckCircleFilled style={{ color: tone }} />;

  return (
    <div style={wrapper} role="status" aria-live="polite">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          {showOwed ? (
            <>
              <div><Text strong>Call completed — outcome not recorded</Text></div>
              <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[outcomeOwed.lead?.name, outcomeOwed.phoneNumber].filter(Boolean).join(' · ') || outcomeOwed.callId}
              </div>
              {backlog > 0 && (
                <div style={{ fontSize: 12, color: owedTone }}>
                  {backlog} more call{backlog > 1 ? 's' : ''} today still awaiting a result.
                </div>
              )}
            </>
          ) : error ? (
            <>
              <div><Text strong style={{ color: tone }}>{error.message}</Text></div>
              {error.hint && error.hint !== error.message && (
                <div><Text type="secondary" style={{ fontSize: 12 }}>{error.hint}</Text></div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text strong>
                  {call.status === 'DISCONNECTED' ? 'Call completed' : meta.label}
                </Text>
                {connected && liveSeconds !== null && (
                  <Tag color="green" style={{ fontVariantNumeric: 'tabular-nums', marginInlineEnd: 0 }}>
                    {formatClock(liveSeconds)}
                  </Tag>
                )}
                {!live && call.durationSeconds > 0 && (
                  <Tag color={measured ? 'green' : 'orange'} style={{ marginInlineEnd: 0 }}>
                    {formatDuration(call.durationSeconds)}{!measured ? ' · confirming' : ''}
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[leadName, number].filter(Boolean).join(' · ') || call.callId}
              </div>
              {/* Said at the only moment the user reaches for a button that is
                  deliberately not there — and it says WHY, because "hang up on
                  the phone" alone reads like a missing feature rather than a
                  setting the rep can change in about ten seconds. */}
              {live && !stopAction && (
                <div style={{ fontSize: 12, color: '#888' }}>
                  Hang up on the phone to end this call. To end calls from here, open the Call
                  Sync app → Settings → <b>Set as default phone app</b>.
                </div>
              )}
              {!live && backlog > 0 && (
                <div style={{ fontSize: 12, color: owedTone }}>
                  {backlog} call{backlog > 1 ? 's' : ''} today still awaiting a result.
                </div>
              )}
            </>
          )}
        </div>

        {lapsed > 0 && (
          <Tooltip title={`${lapsed} call(s) went unsaved past their business day. Under a result-saved rule that target credit can no longer be earned.`}>
            <Tag color="red" style={{ marginInlineEnd: 0 }}>{lapsed} lapsed</Tag>
          </Tooltip>
        )}

        <Space size={4}>
          {/* Two different actions wear this button. Before the phone dials,
              "Cancel" withdraws the command. Once it is dialling, only a
              handset that is the device's default dialer can hang up — so
              "End call" appears only when the phone has actually reported that
              it can. Showing an inert button here is what made the old one look
              broken. */}
          {stopAction && (
            <Button
              size="small"
              danger
              icon={stopAction === 'END' ? <PhoneFilled rotate={135} /> : <CloseCircleOutlined />}
              onClick={onCancel}
            >
              {isMobile ? '' : (stopAction === 'END' ? 'End call' : 'Cancel')}
            </Button>
          )}
          {showOwed && (
            <Button size="small" type="primary" onClick={onAddOutcome}>Add outcome</Button>
          )}
          {(!live || showOwed) && (
            <Button size="small" type="text" icon={<CloseOutlined />} onClick={onDismiss} aria-label="Dismiss" />
          )}
        </Space>
      </div>
    </div>
  );
}
